import React, { useState, useRef, useEffect } from 'react';

const QuestionGridTooltip = ({
    label,
    children,
    description,
    questionStatuses = [],
    type, // 'progression' | 'comprehension' | 'score'
    language,
    className = '',
    // For score mode: answers from the specific session
    scoreAnswers = null,
    scoreDate = null,
    correctAnswers = {}, // qId -> correct answerId
    // For progression mode: answers from active session (enables 3-color mode)
    sessionAnswers = null,
    // Custom content mode: renders this JSX instead of grid content
    customContent = null,
    // Controlled mode props (optional)
    isOpen: controlledIsOpen,
    onToggle
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const ref = useRef(null);

    // Use controlled or uncontrolled mode
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = isControlled ? (val) => onToggle?.(typeof val === 'function' ? val(isOpen) : val) : setInternalIsOpen;

    // Close on click outside (mobile)
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('pointerdown', handler);
        return () => document.removeEventListener('pointerdown', handler);
    }, [isOpen, setIsOpen]);

    // In controlled mode, use JS hover instead of CSS hover
    const handleMouseEnter = () => {
        if (isControlled && window.matchMedia('(min-width: 768px)').matches) {
            setIsOpen(true);
        }
    };
    const handleMouseLeave = () => {
        if (isControlled && window.matchMedia('(min-width: 768px)').matches) {
            setIsOpen(false);
        }
    };

    // Compute counts and statuses based on type
    const isScoreMode = type === 'score' && scoreAnswers;
    const hasActiveSession = type === 'progression' && sessionAnswers !== null && sessionAnswers !== undefined;
    let activeCount = 0;
    let sessionCount = 0;
    let historicOnlyCount = 0;
    let correctCount = 0;
    let incorrectCount = 0;
    let alwaysCorrectCount = 0;
    let sometimesCorrectCount = 0;
    const total = questionStatuses.length;
    const isComprehensionMode = type === 'comprehension';

    if (isScoreMode) {
        questionStatuses.forEach(qs => {
            const userAnswer = scoreAnswers[qs.qId];
            if (userAnswer !== undefined) {
                if (String(userAnswer) === String(correctAnswers[qs.qId])) {
                    correctCount++;
                } else {
                    incorrectCount++;
                }
            }
        });
    } else if (hasActiveSession) {
        questionStatuses.forEach(qs => {
            const ans = sessionAnswers[qs.qId];
            const inSession = ans !== undefined && ans !== 0 && ans !== '0';
            if (inSession) {
                sessionCount++;
            } else if (qs.a) {
                historicOnlyCount++;
            }
        });
        activeCount = sessionCount + historicOnlyCount;
    } else if (isComprehensionMode) {
        questionStatuses.forEach(qs => {
            if (qs.ca) {
                alwaysCorrectCount++;
            } else if (qs.c) {
                sometimesCorrectCount++;
            }
        });
        activeCount = alwaysCorrectCount + sometimesCorrectCount;
    } else {
        activeCount = questionStatuses.filter(q => type === 'progression' ? q.a : q.c).length;
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const hasTooltipContent = customContent || (type === 'score' ? !!scoreAnswers && total > 0 : total > 0);

    if (!hasTooltipContent) {
        return <div className={className}>{children || <span>{label}</span>}</div>;
    }

    return (
        <div
            className={`relative ${isControlled ? '' : 'group'} ${className}`}
            ref={ref}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Trigger: children or label */}
            <div
                onClick={() => setIsOpen(v => !v)}
                className="cursor-help select-none"
            >
                {children || <span>{label}</span>}
            </div>

            {/* Tooltip */}
            <div className={`
                absolute bottom-full left-0 mb-3 z-[100] w-[220px]
                transition-all duration-200 origin-bottom-left
                ${isOpen
                    ? 'opacity-100 scale-100 pointer-events-auto'
                    : `opacity-0 scale-95 pointer-events-none ${!isControlled ? 'md:group-hover:opacity-100 md:group-hover:scale-100 md:group-hover:pointer-events-auto' : ''}`
                }
            `}>
                <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-3 shadow-2xl border border-white/10">
                    {customContent ? customContent : (<>
                    {/* Description */}
                    <p className="text-[10px] text-gray-300 mb-2 leading-relaxed">
                        {description[language]}
                    </p>

                    {/* Date for score mode */}
                    {isScoreMode && scoreDate && (
                        <p className="text-[10px] text-gray-400 mb-2">
                            {formatDate(scoreDate)}
                        </p>
                    )}

                    {/* Counter */}
                    <p className="text-[11px] font-black text-white mb-2">
                        {isScoreMode
                            ? `${correctCount} ${language === 'fr' ? 'juste' : 'correct'} / ${incorrectCount} ${language === 'fr' ? 'faux' : 'wrong'} / ${total - correctCount - incorrectCount} ${language === 'fr' ? 'non rép.' : 'skipped'}`
                            : isComprehensionMode
                                ? `${alwaysCorrectCount} ${language === 'fr' ? 'toujours' : 'always'} / ${sometimesCorrectCount} ${language === 'fr' ? 'parfois' : 'sometimes'} / ${total - activeCount} ${language === 'fr' ? 'jamais' : 'never'}`
                                : hasActiveSession
                                    ? `${sessionCount} / ${total}`
                                    : `${activeCount} / ${total}`
                        }
                    </p>

                    {/* Mini grid */}
                    {total > 0 && (
                        <div className="grid grid-cols-10 gap-[3px]">
                            {questionStatuses.map((qs, idx) => {
                                let cellColor = 'bg-gray-600';

                                if (isScoreMode) {
                                    const userAnswer = scoreAnswers[qs.qId];
                                    if (userAnswer !== undefined) {
                                        if (String(userAnswer) === String(correctAnswers[qs.qId])) {
                                            cellColor = 'bg-emerald-500';
                                        } else {
                                            cellColor = 'bg-red-500';
                                        }
                                    }
                                } else if (hasActiveSession && type === 'progression') {
                                    // 3-color mode: gray (never) / light blue (historic) / blue (session)
                                    const ans = sessionAnswers[qs.qId];
                                    const inSession = ans !== undefined && ans !== 0 && ans !== '0';
                                    if (inSession) {
                                        cellColor = 'bg-blue-500';
                                    } else if (qs.a) {
                                        cellColor = 'bg-sky-300';
                                    }
                                } else if (isComprehensionMode) {
                                    if (qs.ca) {
                                        cellColor = 'bg-purple-500';
                                    } else if (qs.c) {
                                        cellColor = 'bg-purple-300';
                                    }
                                } else {
                                    const active = type === 'progression' ? qs.a : qs.c;
                                    if (active) {
                                        cellColor = 'bg-blue-500';
                                    }
                                }

                                return (
                                    <div
                                        key={idx}
                                        className={`w-[14px] h-[14px] rounded-sm ${cellColor}`}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* Legend */}
                    {isScoreMode ? (
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-white/10">
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-emerald-500"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Juste' : 'Correct'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-red-500"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Faux' : 'Wrong'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Non rép.' : 'Skip'}</span>
                            </div>
                        </div>
                    ) : isComprehensionMode ? (
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-white/10">
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-purple-500"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Toujours' : 'Always'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-purple-300"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Parfois' : 'Sometimes'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Jamais' : 'Never'}</span>
                            </div>
                        </div>
                    ) : hasActiveSession ? (
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-white/10">
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-blue-500"></div>
                                <span className="text-[8px] text-gray-400">Session</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-sky-300"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Déjà vu' : 'Seen'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Jamais vu' : 'Unseen'}</span>
                            </div>
                        </div>
                    ) : null}
                    </>)}
                </div>
                {/* Arrow */}
                <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 border-r border-b border-white/10" />
            </div>
        </div>
    );
};

export default QuestionGridTooltip;
